import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { type ReactNode, Suspense, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { loginRequest } from '@/authConfig';
import { publicRoutes } from '@/constants';
import DashboardLayout from '@/layouts/DashboardLayout';
import Billing from '@/pages/Billing';
import Contacts from '@/pages/Contacts';
import ContactDetails from '@/pages/Contacts/ContactDetails';
import Dashboard from '@/pages/Dashboard';
import ClientApprovalPage from '@/pages/EmailResponsePages/ClientResponsePages/ClientApprovalPage';
import LawyerApprovalPage from '@/pages/EmailResponsePages/LawyerResponsePages/LawyerApprovalPage';
import ExtensionPrivacyPolicy from '@/pages/ExtensionPrivacyPolicy';
import LawFirms from '@/pages/LawFirms';
import LinkRedirectPage from '@/pages/LinkRedirectPage';
import NotFound from '@/pages/NotFound';
import Onboarding from '@/pages/Onboarding';
import ProspectDetails from '@/pages/Prospects/ProspectDetails/ProspectDetails';
import Prospects from '@/pages/Prospects/Prospects';
import QuoteIntake from '@/pages/QuoteIntake';
import IntakeOnlySetup from '@/pages/QuoteIntake/IntakeOnlySetup';
import QuoteIntakeSetup from '@/pages/QuoteIntake/QuoteIntakeSetup';
import ClientFormErrorBoundary from '@/pages/QuoteIntakeClientView/ClientFormErrorBoundary';
import IntakeFormParent from '@/pages/QuoteIntakeClientView/IntakeFormParent';
import QuoteFormView from '@/pages/QuoteIntakeClientView/QuoteFormView';
import Settings from '@/pages/Settings';
import StandAloneIDV from '@/pages/StandAloneIDV';
import StandAloneIDVDetails from '@/pages/StandAloneIDV/StandAloneIDVDetails';
import Templates from '@/pages/Templates';
import Users from '@/pages/Users';
import UserDetails from '@/pages/Users/UserDetails';
import Logout from './pages/Logout';
import UserDetailsSkeleton from './pages/Users/UserDetails/UserDetailsSkeleton';

function isPublicRoute(routePatterns: string[], pathname: string) {
    // Match the current location against the allowed public route prefixes
    return routePatterns.some((pattern) => pathname.startsWith(pattern));
}

function suspenseRoute(routeElement: ReactNode, fallbackElement?: ReactNode) {
    return <Suspense fallback={fallbackElement}>{routeElement}</Suspense>;
}

function ProspectDetailsRoute() {
    const { prospectId } = useParams();
    return <ProspectDetails key={prospectId} />;
}

function UserDetailsRoute() {
    const { userId } = useParams();
    return <UserDetails key={userId} />;
}

function ContactDetailsRoute() {
    const { contactId } = useParams();
    return <ContactDetails key={contactId} />;
}

function IntakeOnlySetupRoute() {
    const { quoteTemplateId } = useParams();
    return <IntakeOnlySetup key={quoteTemplateId} />;
}

function QuoteIntakeRoute() {
    const { quoteIntakeId } = useParams();
    return <QuoteIntake key={quoteIntakeId} />;
}

function StandAloneIDVDetailsRoute() {
    const { prospectId } = useParams();
    return <StandAloneIDVDetails key={prospectId} />;
}

export default function RoutesPage() {
    const { instance, accounts, inProgress: msalInProgress } = useMsal();
    const location = useLocation();
    const redirectInProgress = useRef(false);

    useEffect(() => {
        const shouldRedirect =
            accounts.length === 0 &&
            msalInProgress === InteractionStatus.None &&
            !isPublicRoute(publicRoutes, location.pathname) &&
            !redirectInProgress.current;

        if (shouldRedirect) {
            redirectInProgress.current = true;
            instance.loginRedirect(loginRequest);
        }
    }, [accounts, msalInProgress, location.pathname]); // oxlint-disable-line react-hooks/exhaustive-deps -- instance is sometimes created by MSAL but should not trigger an effect

    if (accounts.length > 0) {
        return (
            <Routes>
                <Route element={<Onboarding />} path="/onboarding"></Route>
                <Route
                    path="/quote/view/:quoteTemplateId/:lawFirmId"
                    element={
                        <ClientFormErrorBoundary>
                            <QuoteFormView />
                        </ClientFormErrorBoundary>
                    }
                ></Route>
                <Route
                    path="/intake/view/:intakeTemplateId/:lawFirmId"
                    element={<IntakeFormParent />}
                ></Route>
                <Route
                    path="/lawyerapproval/:instanceId/:prospectId"
                    element={<LawyerApprovalPage />}
                ></Route>
                <Route
                    path="/clientapproval/:instanceId/:prospectId"
                    element={<ClientApprovalPage />}
                ></Route>
                <Route path="/q/:sixDigitIdentifier" element={<LinkRedirectPage />}></Route>
                <Route path="/i/:sixDigitIdentifier" element={<LinkRedirectPage />}></Route>
                <Route
                    path="/extension-privacy-policy"
                    element={<ExtensionPrivacyPolicy />}
                ></Route>
                <Route element={<DashboardLayout />}>
                    <Route path="/quote/:quoteIntakeId" element={<QuoteIntakeRoute />} />
                    {/* Public routes */}
                    <Route path={'/'} element={suspenseRoute(<Prospects />)} />
                    <Route path={'/dashboard'} element={suspenseRoute(<Dashboard />)} />
                    <Route path={'/not-found'} element={suspenseRoute(<NotFound />)} />
                    <Route path={'/logout'} element={suspenseRoute(<Logout />)} />
                    {/* Private routes */}
                    <Route path={'/billing'} element={suspenseRoute(<Billing />)} />
                    <Route path={'/quote-intake'} element={suspenseRoute(<QuoteIntakeRoute />)}>
                        <Route
                            path={'/quote-intake/setup'}
                            element={suspenseRoute(<QuoteIntakeSetup />)}
                        />
                    </Route>
                    <Route path={'/templates/:tab'} element={suspenseRoute(<Templates />)} />
                    <Route path={'/users'} element={suspenseRoute(<Users />)} />
                    <Route path={'/law-firms'} element={suspenseRoute(<LawFirms />)} />
                    <Route path={'/contacts'} element={suspenseRoute(<Contacts />)} />
                    <Route
                        path={'/contacts/:contactId'}
                        element={suspenseRoute(<ContactDetailsRoute />)}
                    />
                    <Route
                        path={'/user/:userId'}
                        element={suspenseRoute(<UserDetailsRoute />, <UserDetailsSkeleton />)}
                    />
                    <Route path={'/prospects'} element={suspenseRoute(<Prospects />)} />
                    <Route
                        path={'/prospects/:prospectId'}
                        element={<Navigate to="summary" replace />}
                    />
                    <Route
                        path={'/prospects/:prospectId/:prospectTabs'}
                        element={suspenseRoute(<ProspectDetailsRoute />)}
                    />
                    <Route
                        path={'/intake-only/setup/:step'}
                        element={suspenseRoute(<IntakeOnlySetupRoute />)}
                    />
                    <Route
                        path={'/intake-only/setup/:step/:quoteTemplateId'}
                        element={suspenseRoute(<IntakeOnlySetupRoute />)}
                    />
                    <Route path={'/settings/:tab'} element={suspenseRoute(<Settings />)} />
                    <Route path={'/idv'} element={suspenseRoute(<StandAloneIDV />)} />
                    <Route
                        path={'/idv/:prospectId'}
                        element={<Navigate to="id-verification" replace />}
                    />
                    <Route
                        path={'/idv/:prospectId/:idvTabs'}
                        element={suspenseRoute(<StandAloneIDVDetailsRoute />)}
                    />
                </Route>
                <Route path="*" element={<NotFound />} />
            </Routes>
        );
    } else if (msalInProgress === 'login') {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <h4>Please wait while we check login...</h4>
            </div>
        );
    } else if (msalInProgress === 'logout') {
        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                <h4>Logging out...</h4>
            </div>
        );
    } else {
        return (
            <Routes>
                <Route
                    path="/quote/view/:quoteTemplateId/:lawFirmId"
                    element={
                        <ClientFormErrorBoundary>
                            <QuoteFormView />
                        </ClientFormErrorBoundary>
                    }
                ></Route>
                <Route
                    path="/intake/view/:intakeTemplateId/:lawFirmId"
                    element={<IntakeFormParent />}
                ></Route>
                <Route
                    path="/lawyerapproval/:instanceId/:prospectId"
                    element={<LawyerApprovalPage />}
                ></Route>
                <Route
                    path="/clientapproval/:instanceId/:prospectId"
                    element={<ClientApprovalPage />}
                ></Route>
                <Route path="/q/:sixDigitIdentifier" element={<LinkRedirectPage />}></Route>
                <Route path="/i/:sixDigitIdentifier" element={<LinkRedirectPage />}></Route>
                <Route
                    path="/extension-privacy-policy"
                    element={<ExtensionPrivacyPolicy />}
                ></Route>
                {msalInProgress === 'none' && <Route path="*" element={<NotFound />}></Route>}
            </Routes>
        );
    }
}
